import { BaseApi } from '../BaseApi/BaseApi';
import { ApiManager } from '../ApiManager/ApiManager';

export interface IClinicSeatBilling {
    clinicID: number;
    clinicName: string;
    billingPeriod: string;
    activeSeats: number;
    totalBillableSeats: number;
    pricePerSeat: number;
    prorationFactor: number;
    totalAmount: number;
    earliestUserActivationDate?: string;
    contactName: string;
    contactEmail: string;
}

export interface IClinicSeatBillingReport {
    billingPeriod: string;
    clinics: IClinicSeatBilling[];
    totalClinics: number;
    totalSeats: number;
    totalAmount: number;
    generatedDateTime: string;
    generatedByUserID: number;
}

export interface IClinicSeatBillingReportResponse {
    success: boolean;
    report: IClinicSeatBillingReport;
}

export interface IBillingPeriod {
    period: string;
    displayName: string;
    year: number;
    month: number;
    isCurrentMonth: boolean;
    isPreviousMonth: boolean;
}

export interface IAvailablePeriods {
    success: boolean;
    periods: IBillingPeriod[];
    defaultPeriod: string;
}

export class ClinicSeatBillingApi extends BaseApi {
    /**
     * Generate clinic seat billing report for preview (without CSV export)
     */
    async getBillingReport(billingPeriod: string): Promise<IClinicSeatBillingReportResponse> {
        try {
            const response = await ApiManager.get(`/ClinicSeatBilling/Report/${billingPeriod}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching clinic seat billing report:', error);
            throw error;
        }
    }

    /**
     * Export clinic seat billing report as CSV download
     */
    async exportBillingReportCSV(billingPeriod: string): Promise<void> {
        try {
            const response = await ApiManager.get(`/ClinicSeatBilling/Export/${billingPeriod}`, {
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from response headers or generate default
            const disposition = response.headers['content-disposition'];
            let filename = `clinic_seat_billing_${billingPeriod}.csv`;
            
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                if (matches != null && matches[1]) 
                    filename = matches[1].replace(/['"]/g, '');
                
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting clinic seat billing CSV:', error);
            throw error;
        }
    }

    /**
     * Get available billing periods for the month selector
     */
    async getAvailablePeriods(): Promise<IAvailablePeriods> {
        try {
            const response = await ApiManager.get('/ClinicSeatBilling/AvailablePeriods');
            return response.data;
        } catch (error) {
            console.error('Error fetching available billing periods:', error);
            throw error;
        }
    }

    /**
     * Get billing summary across multiple periods
     */
    async getBillingSummary(startPeriod: string, endPeriod: string): Promise<any> {
        try {
            const response = await ApiManager.get('/ClinicSeatBilling/Summary', {
                params: { startPeriod, endPeriod }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching billing summary:', error);
            throw error;
        }
    }
}
